import axios from "axios";

/**
 * @typedef {Object} TokenStorageProvider
 * @property {() => string | null} getToken
 * @property {() => string | null} getRefreshToken
 * @property {(accessToken: string, refreshToken?: string) => void} setTokens
 * @property {() => void} clearTokens
 */

export const defaultTokenStorageProvider = {
  getToken: () =>
    typeof globalThis.localStorage !== "undefined"
      ? globalThis.localStorage.getItem("homy_token")
      : null,
  getRefreshToken: () =>
    typeof globalThis.localStorage !== "undefined"
      ? globalThis.localStorage.getItem("homy_refresh_token")
      : null,
  setTokens: (accessToken, refreshToken) => {
    if (typeof globalThis.localStorage !== "undefined") {
      if (accessToken) globalThis.localStorage.setItem("homy_token", accessToken);
      if (refreshToken) globalThis.localStorage.setItem("homy_refresh_token", refreshToken);
    }
  },
  clearTokens: () => {
    if (typeof globalThis.localStorage !== "undefined") {
      globalThis.localStorage.removeItem("homy_token");
      globalThis.localStorage.removeItem("homy_refresh_token");
      globalThis.localStorage.removeItem("homy_user");
    }
  },
};

/**
 * Tenant API Client for multi-tenant customer applications.
 * Handles Bearer token authentication and 401 retry queueing.
 */
export function createTenantClient(options = {}) {
  const baseURL = options.baseURL || "/api";
  const storage = options.tokenStorage || defaultTokenStorageProvider;
  const getToken = options.getToken || (() => storage.getToken());
  const getRefreshToken = options.getRefreshToken || (() => storage.getRefreshToken());
  const setTokens = options.setTokens || ((acc, ref) => storage.setTokens(acc, ref));
  const clearTokens = options.clearTokens || (() => storage.clearTokens());

  const client = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  // Request interceptor: attach bearer token
  client.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  // Response interceptor: handle 401 with token refresh queue
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        const requestUrl = originalRequest.url || "";
        if (
          requestUrl.includes("/auth/refresh") ||
          requestUrl.includes("/auth/local")
        ) {
          clearTokens();
          return Promise.reject(error);
        }

        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          clearTokens();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return client(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await axios.post(`${baseURL}/core/auth/refresh`, {
            refreshToken,
          });

          const newToken = response.data?.token || response.data?.accessToken;
          const newRefreshToken = response.data?.refreshToken;

          setTokens(newToken, newRefreshToken);

          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return client(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          clearTokens();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
}

/**
 * @deprecated Use createTenantClient with explicit TokenStorageProvider or use generated typed clients from @/api
 */
export const tenantClient = createTenantClient();
